import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '../ui';
import { useTranslation } from '../context/TranslationContext';
import styles from './SectionedTable.module.css';

const PAGE_SIZE_DEFAULT = 50;

/**
 * SectionedTable — a sortable, filterable, grouped data table with
 * progressive rendering (infinite scroll).
 *
 * Column shape:
 *   {
 *     key:            string,
 *     header:         ReactNode | () => ReactNode,
 *     accessor:       (row) => any,            // value for sorting / default filter
 *     cell?:          (row) => ReactNode,       // custom cell render
 *     sortable?:      boolean,                  // default true
 *     filterable?:    boolean,                  // default true (requires accessor)
 *     filterValue?:   (row) => string,          // searchable string (falls back to String(accessor))
 *     align?:         'start' | 'end',
 *     className?:     string,
 *     headerClassName?: string,
 *     bodyClassName?:  string,
 *   }
 *
 * groupBy:
 *   {
 *     key:       (row) => string,
 *     label:     (row, key) => ReactNode,
 *     order?:    (a, b) => number,
 *     trailing?: (rows, key) => ReactNode
 *   }
 */
const SectionedTable = ({
  data = [],
  columns = [],
  groupBy = null,
  initialSort = null,
  rowKey = (row) => row.id,
  rowClassName,
  onRowClick,
  emptyMessage = '',
  className = '',
  pageSize = PAGE_SIZE_DEFAULT,
  enableColumnFilters = false,
}) => {
  const { t } = useTranslation();
  const [sort, setSort] = useState(initialSort);
  const [columnFilters, setColumnFilters] = useState({});
  const effectivePageSize = pageSize || PAGE_SIZE_DEFAULT;
  const [visibleCount, setVisibleCount] = useState(effectivePageSize);
  const sentinelRef = useRef(null);

  useEffect(() => {
    setVisibleCount(effectivePageSize);
  }, [data, columnFilters, sort, effectivePageSize]);

  const handleFilterChange = useCallback((colKey, value) => {
    setColumnFilters((prev) => ({ ...prev, [colKey]: value }));
  }, []);

  const canSort = (col) => col.sortable !== false;
  const canFilter = (col) => col.filterable !== false && !!col.accessor;

  const handleSort = (col) => {
    if (!canSort(col)) return;
    setSort((prev) => {
      if (!prev || prev.key !== col.key) return { key: col.key, dir: 'asc' };
      if (prev.dir === 'asc') return { key: col.key, dir: 'desc' };
      return null;
    });
  };

  const compare = (a, b, accessor) => {
    const av = accessor(a);
    const bv = accessor(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return av - bv;
    if (av instanceof Date && bv instanceof Date) return av.getTime() - bv.getTime();
    return String(av).localeCompare(String(bv));
  };

  const filteredData = useMemo(() => {
    if (!enableColumnFilters) return data;
    const active = Object.entries(columnFilters).filter(([, v]) => v && v.trim());
    if (active.length === 0) return data;
    return data.filter((row) =>
      active.every(([key, query]) => {
        const col = columns.find((c) => c.key === key);
        if (!col) return true;
        const getter = col.filterValue || col.accessor;
        if (!getter) return true;
        return String(getter(row) ?? '')
          .toLowerCase()
          .includes(query.trim().toLowerCase());
      }),
    );
  }, [data, columnFilters, columns, enableColumnFilters]);

  const sortedRows = useMemo(() => {
    if (!sort) return filteredData;
    const col = columns.find((c) => c.key === sort.key);
    if (!col || !col.accessor) return filteredData;
    const out = [...filteredData].sort((a, b) => compare(a, b, col.accessor));
    return sort.dir === 'desc' ? out.reverse() : out;
  }, [filteredData, columns, sort]);

  const allGrouped = useMemo(() => {
    if (!groupBy) return null;
    const groups = new Map();
    for (const row of sortedRows) {
      const k = groupBy.key(row);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(row);
    }
    const keys = Array.from(groups.keys());
    const order = groupBy.order || ((a, b) => String(b).localeCompare(String(a)));
    keys.sort(order);
    return keys.map((k) => ({ key: k, rows: groups.get(k) }));
  }, [sortedRows, groupBy]);

  const visibleGrouped = useMemo(() => {
    if (!allGrouped) return null;
    let emitted = 0;
    const result = [];
    for (const group of allGrouped) {
      if (emitted >= visibleCount) break;
      const remaining = visibleCount - emitted;
      const visibleRows = group.rows.slice(0, remaining);
      result.push({ key: group.key, allRows: group.rows, visibleRows });
      emitted += visibleRows.length;
    }
    return result;
  }, [allGrouped, visibleCount]);

  const totalRows = sortedRows.length;
  const hasMore = visibleCount < totalRows;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + effectivePageSize, totalRows));
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, effectivePageSize, totalRows]);

  const showFilterRow = enableColumnFilters && columns.some(canFilter);

  const renderHeader = () => (
    <thead>
      <tr className={showFilterRow ? styles.headerWithFilters : ''}>
        {columns.map((col) => {
          const isSorted = sort && sort.key === col.key;
          const sortEnabled = canSort(col);
          return (
            <th
              key={col.key}
              className={`${col.headerClassName || ''} ${col.align === 'end' ? styles.alignEnd : ''}`}
              onClick={sortEnabled ? () => handleSort(col) : undefined}
              style={{ cursor: sortEnabled ? 'pointer' : 'default' }}
              aria-sort={
                !isSorted ? 'none' : sort.dir === 'asc' ? 'ascending' : 'descending'
              }
            >
              <span className={styles.headerInner}>
                {typeof col.header === 'function' ? col.header() : col.header}
                {sortEnabled && (
                  <span className={styles.sortIcon}>
                    {isSorted ? (
                      <Icon
                        name={sort.dir === 'asc' ? 'fa-chevron-up' : 'fa-chevron-down'}
                        size={12}
                      />
                    ) : (
                      <span className={styles.sortPlaceholder}>
                        <Icon name="fa-chevron-up" size={10} />
                        <Icon name="fa-chevron-down" size={10} />
                      </span>
                    )}
                  </span>
                )}
              </span>
            </th>
          );
        })}
      </tr>
      {showFilterRow && (
        <tr className={styles.filterRow}>
          {columns.map((col) => {
            const filterVal = columnFilters[col.key] || '';
            const headerLabel = typeof col.header === 'string' ? col.header : col.key;
            return (
              <th
                key={`filter-${col.key}`}
                className={col.align === 'end' ? styles.alignEnd : ''}
              >
                {canFilter(col) ? (
                  <div
                    className={[
                      styles.filterInputWrapper,
                      filterVal ? styles.filterInputWrapperActive : '',
                      col.align === 'end' ? styles.filterInputWrapperEnd : '',
                    ].join(' ')}
                  >
                    <Icon name="fa-search" size={11} className={styles.filterSearchIcon} />
                    <input
                      type="text"
                      size={1}
                      className={styles.filterInput}
                      value={filterVal}
                      onChange={(e) => handleFilterChange(col.key, e.target.value)}
                      placeholder={t('search')}
                      aria-label={`Filter ${headerLabel}`}
                    />
                    {filterVal && (
                      <button
                        type="button"
                        className={styles.filterClearBtn}
                        onClick={() => handleFilterChange(col.key, '')}
                        aria-label={`Clear ${headerLabel} filter`}
                      >
                        <Icon name="fa-times" size={9} />
                      </button>
                    )}
                  </div>
                ) : null}
              </th>
            );
          })}
        </tr>
      )}
    </thead>
  );

  const renderCell = (row, col) => {
    const value = col.cell ? col.cell(row) : col.accessor ? col.accessor(row) : null;
    return (
      <td
        key={col.key}
        className={`${col.bodyClassName || col.className || ''} ${col.align === 'end' ? styles.alignEnd : ''}`}
      >
        {value}
      </td>
    );
  };

  const renderRows = (rows) =>
    rows.map((row) => (
      <tr
        key={rowKey(row)}
        className={typeof rowClassName === 'function' ? rowClassName(row) : ''}
        onClick={onRowClick ? () => onRowClick(row) : undefined}
        style={{ cursor: onRowClick ? 'pointer' : 'default' }}
      >
        {columns.map((col) => renderCell(row, col))}
      </tr>
    ));

  const colSpan = columns.length;

  if (sortedRows.length === 0) {
    return (
      <div className={`${styles.wrapper} ${className}`}>
        <table className={styles.table}>
          {renderHeader()}
          <tbody>
            <tr>
              <td colSpan={colSpan} className={styles.empty}>
                {emptyMessage}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={`${styles.wrapper} ${className}`}>
      <table className={styles.table}>
        {renderHeader()}
        {visibleGrouped ? (
          visibleGrouped.map((group) => (
            <tbody key={group.key} className={styles.sectionBody}>
              <tr className={styles.sectionHeader}>
                <td colSpan={colSpan}>
                  <div className={styles.sectionHeaderInner}>
                    <span className={styles.sectionTitle}>
                      {groupBy.label
                        ? groupBy.label(group.allRows[0], group.key)
                        : group.key}
                    </span>
                    {groupBy.trailing && (
                      <span className={styles.sectionTrailing}>
                        {groupBy.trailing(group.allRows, group.key)}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
              {renderRows(group.visibleRows)}
            </tbody>
          ))
        ) : (
          <tbody>{renderRows(sortedRows.slice(0, visibleCount))}</tbody>
        )}
      </table>
      {hasMore && (
        <div ref={sentinelRef} className={styles.sentinel}>
          <span className={styles.sentinelDots}>•••</span>
        </div>
      )}
    </div>
  );
};

export default SectionedTable;
